import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import { blogPosts } from "@/data";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2 className="article-body__h2">{block.text}</h2>;
    case "h3":
      return <h3 className="article-body__h3">{block.text}</h3>;
    case "p":
      return <p className="article-body__p">{block.text}</p>;
    case "ul":
      return (
        <ul className="article-body__list">
          {block.items.map((item, i) => (
            <li key={i} className="article-body__li">
              {typeof item === "string" ? (
                item
              ) : (
                <>
                  <span className="article-body__lead">{item.lead}:</span> {item.text}
                </>
              )}
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
};

const ArticlePage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/insights" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover,
    articleSection: post.category,
    url: `https://www.hitours.group/insights/${post.slug}`,
    mainEntityOfPage: `https://www.hitours.group/insights/${post.slug}`,
    author: { "@type": "Organization", name: "HI Tours Group" },
    publisher: {
      "@type": "Organization",
      name: "HI Tours Group",
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.prod.website-files.com/688091f7e34bfbf3e9bda976/6891aa981164f1d025be9b64_HI%20Tours%20Group%20%20Logo%20Color.svg",
      },
    },
  };

  return (
    <main className="page article-page" data-testid="article-page">
      <Seo
        title={post.title}
        path={`/insights/${post.slug}`}
        description={post.excerpt}
        image={post.cover}
        type="article"
        jsonLd={articleJsonLd}
      />
      <article>
        <header className="article-hero">
          <div className="container article-hero__inner">
            <Link to="/insights" className="article-back" data-testid="article-back-link">
              <ArrowLeft size={18} aria-hidden="true" />
              All insights
            </Link>
            <span className="article-hero__cat" data-testid="article-category">{post.category}</span>
            <h1 className="article-hero__title" data-testid="article-title">{post.title}</h1>
            {post.readTime && <span className="article-hero__meta">{post.readTime}</span>}
          </div>
        </header>

        <div className="container">
          <figure className="article-cover">
            <img src={post.cover} alt={post.title} loading="lazy" />
          </figure>
        </div>

        <div className="container">
          <div className="article-body" data-testid="article-body">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </div>
      </article>

      <section className="section article-related">
        <div className="container">
          <h2 className="blog-section-title">More insights</h2>
          <div className="blog-grid">
            {related.map((p) => (
              <Link className="blog-card reveal" key={p.slug} to={`/insights/${p.slug}`} data-testid={`related-card-${p.slug}`}>
                <div className="blog-card__media">
                  <img src={p.cover} alt={p.title} loading="lazy" />
                  <span className="blog-card__cat">{p.category}</span>
                </div>
                <div className="blog-card__body">
                  <h3 className="blog-card__title">{p.title}</h3>
                  <p className="blog-card__excerpt">{p.excerpt}</p>
                  <span className="blog-card__link">Read article
                    <span className="icon-plus icon-plus--sm">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>
                    </span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default ArticlePage;
